import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { Form } from '@unform/web';
import PropTypes from 'prop-types';
import * as Yup from 'yup';

import { Spinner } from '~/components';
import { Input } from '~/components/Form';
import { t, validCharacters } from '~/helpers';
import {
  storeMarcasVeiculo,
  updateMarcasVeiculo,
} from '~/store/modules/marcasveiculo/actions';

export default function CreateOrUpdate({ reference, data, create }) {
  const dispatch = useDispatch();

  const { client } = useSelector((state) => state.user.profile);
  const { loading, find, params } = useSelector((state) => state.marcasveiculo);

  const initials = create ? data : { ...find, ...data };

  async function handleSubmit(param) {
    try {
      const schema = Yup.object().shape({
        nomemarcaveiculo: Yup.string()
          .trim()
          .required(t('s.geral.validacao.campoobrigatorio'))
          .min(1, t('s.geral.validacao.campoobrigatorio'))
          .max(30, t('s.geral.validacao.tamanhomaximo').replace('%s', 30))
          .test(
            'characters-invalid',
            t('s.configuracao.geral.naopodecontercaracterespecial'),
            (value) => validCharacters(value)
          ),

        codigoauxiliarmarcaveiculo: Yup.string()
          .trim()
          .required(t('s.geral.validacao.campoobrigatorio'))
          .min(1, t('s.geral.validacao.campoobrigatorio'))
          .max(20, t('s.geral.validacao.tamanhomaximo').replace('%s', 20))
          .test(
            'characters-invalid',
            t('s.configuracao.geral.naopodecontercaracterespecial'),
            (value) => validCharacters(value)
          ),

        codigofipemarcaveiculo: Yup.string()
          .ensure()
          .trim()
          .nullable()
          .max(20, t('s.geral.validacao.tamanhomaximo').replace('%s', 20))
          .test(
            'characters-invalid',
            t('s.configuracao.geral.naopodecontercaracterespecial'),
            (value) => validCharacters(value)
          ),
      });

      await schema.validate(param, {
        abortEarly: false,
      });

      const objSend = {
        ...initials,
        ...param,
        idcliente: client,
      };

      if (create) {
        await dispatch(storeMarcasVeiculo(objSend, params));
      } else {
        await dispatch(updateMarcasVeiculo(objSend, params));
      }
    } catch (err) {
      const validationErrors = {};

      if (err instanceof Yup.ValidationError) {
        err.inner.forEach((error) => {
          validationErrors[error.path] = error.message.replace(
            `${error.path} `,
            ''
          );
        });

        reference.current.setErrors(validationErrors);
      }
    }
  }

  useEffect(() => {
    if (!loading) {
      reference.current.setData(initials);
    }
  }, [loading, initials]);

  return (
    <>
      {loading ? (
        <div style={{ height: '14rem', width: '50rem' }}>
          <Spinner loading />
        </div>
      ) : (
        <Form ref={reference} initialData={initials} onSubmit={handleSubmit}>
          <Input
            name="codigoauxiliarmarcaveiculo"
            label={t('s.cadastro.marcasveiculo.codigoauxiliarmarcaveiculo')}
          />

          <Input
            name="nomemarcaveiculo"
            label={t('s.cadastro.marcasveiculo.nomemarcaveiculo')}
          />

          <Input
            name="codigofipemarcaveiculo"
            label={t('s.cadastro.marcasveiculo.codigofipemarcaveiculo')}
          />
        </Form>
      )}
    </>
  );
}

CreateOrUpdate.propTypes = {
  reference: PropTypes.objectOf(PropTypes.any).isRequired,
  create: PropTypes.bool.isRequired,
  data: PropTypes.objectOf(PropTypes.any),
};

CreateOrUpdate.defaultProps = {
  data: {},
};
